import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getExtraProducts } from "../store/extraProducts/selectors";
import usePaxWithBaggages from "../../hooks/usePaxWithBaggages";
import useIsBrandedFare from "../../branded-fares/hooks/useIsBrandedFare";
import { getIsFlexibleTicketPreSelectedAtMeta } from "../../store/selectors/checkoutSteps";
import { getCartDetails } from "../../store/cartDetails/selectors";
import useMultiProduct from "../../hooks/useMultiProduct";

export default function useExtraProductsUtils() {
  const { isPaxWithBaggages } = usePaxWithBaggages();
  const extraProducts = useSelector(getExtraProducts);
  const cartDetails = useSelector(getCartDetails);
  const isBrandedFare = useIsBrandedFare();
  const isMPEnabled = useMultiProduct(cartDetails?.cart?.supplyType);
  const isFlexibleTicketPreselectedAtMeta = useSelector(getIsFlexibleTicketPreSelectedAtMeta);
  const canOfferFlexibleTicket = isBrandedFare && !isFlexibleTicketPreselectedAtMeta;
  const cfarAvailable = !!extraProducts?.cancelForAnyReason;

  const baggage = useMemo(() => {
    return !isPaxWithBaggages;
  }, [isPaxWithBaggages]);

  const meal = useMemo(() => {
    return !!extraProducts?.mealPreference;
  }, [extraProducts]);

  const insuranceMP = useMemo(() => {
    return isMPEnabled && !!cartDetails?.basketReference;
  }, [isMPEnabled, cartDetails?.basketReference]);

  const insurance = useMemo(() => {
    return !!extraProducts?.travelInsurance?.options && Object.keys(extraProducts.travelInsurance.options).length > 0;
  }, [extraProducts]);

  const cfar = useMemo(() => {
    return cfarAvailable && canOfferFlexibleTicket;
  }, [cfarAvailable, canOfferFlexibleTicket]);

  const fastTrack = useMemo(() => {
    return extraProducts?.fastTrack;
  }, [extraProducts]);

  const flexTicket = useMemo(() => {
    return !!extraProducts?.flexibleTicket && canOfferFlexibleTicket;
  }, [extraProducts, canOfferFlexibleTicket]);

  const isAnyExtras = useMemo(
    () =>
      !!(
        cfarAvailable
          ? [baggage, meal, insuranceMP, insurance, cfar, fastTrack]
          : [baggage, meal, flexTicket, insuranceMP, insurance, fastTrack]
      ).filter(Boolean).length,
    [baggage, meal, insuranceMP, insurance, cfar, fastTrack, flexTicket, cfarAvailable]
  );

  return { isAnyExtras };
}
